import { Palette, GripVertical, Smartphone, Sparkles, Image } from "lucide-react";
import { CandyButton } from "../playful/CandyButton";
import { GeometricBadge } from "../playful/GeometricBadge";
import { FloatingIcon } from "../playful/FloatingIcon";
import { Blob } from "../playful/Decor";
import { Reveal } from "./Reveal";
import { PhoneBioMock } from "./mocks";

// A few real themes from themeEngine, represented as swatches.
const THEME_SWATCHES = [
  { name: "Minimal", tier: "Free", bg: "#ffffff", accent: "#1E293B" },
  { name: "Fresh", tier: "Free", bg: "#F0FDF4", accent: "#16A34A" },
  { name: "Candy", tier: "Pro", bg: "#F3EEFF", accent: "#8B5CF6" },
  { name: "Sunset", tier: "Pro", bg: "linear-gradient(135deg,#FFF3E0,#FCE4EC)", accent: "#F06292" },
  { name: "Neon", tier: "Pro", bg: "#050A0E", accent: "#00FF88" },
  { name: "Aurora", tier: "Ent", bg: "linear-gradient(135deg,#0F172A,#3B0764)", accent: "#A78BFA" },
];

const PERKS = [
  { icon: GripVertical, label: "Drag-reorder links" },
  { icon: Image, label: "Avatar & branding" },
  { icon: Smartphone, label: "Live mobile preview" },
  { icon: Sparkles, label: "Animated themes" },
];

/** CreatorSection — the bio-page / creator pillar. */
export default function CreatorSection() {
  return (
    <section
      className="overflow-hidden border-y-2 border-ink bg-pink-tint"
      data-testid="creator-section"
    >
      <div className="mx-auto max-w-[1400px] px-5 py-20 md:px-10">
        <div className="flex flex-col items-center gap-10 lg:flex-row">
          {/* Phone preview + theme studio */}
          <div className="relative order-2 w-full min-w-0 lg:order-1 lg:w-1/2">
            <Blob
              tone="pink"
              size={260}
              className="pointer-events-none absolute -left-10 -top-12 animate-drift opacity-40"
            />
            <Reveal className="relative flex min-w-0 flex-col items-center justify-center gap-8 sm:flex-row">
              <PhoneBioMock
                theme={{
                  bg: "linear-gradient(160deg,#F3EEFF 0%,#FCE7F3 100%)",
                  accent: "#8B5CF6",
                  font: "'Outfit', sans-serif",
                }}
              />

              {/* Theme studio card */}
              <div className="w-full max-w-full rounded-3xl border-2 border-ink bg-white p-5 shadow-sticker-md sm:w-auto">
                <div className="mb-4 flex items-center gap-2">
                  <FloatingIcon icon={Palette} tone="candy" size={36} />
                  <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
                    THEME STUDIO
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2.5">
                  {THEME_SWATCHES.map((t) => (
                    <div key={t.name} className="flex flex-col items-center gap-1">
                      <div
                        className="relative h-12 w-12 overflow-hidden rounded-2xl border-2 border-ink shadow-sticker-xs"
                        style={{ background: t.bg }}
                      >
                        <span
                          className="absolute bottom-1 right-1 h-3 w-3 rounded-full border border-ink"
                          style={{ background: t.accent }}
                        />
                      </div>
                      <span className="font-mono text-[8px] uppercase tracking-widest text-ink/60">
                        {t.name}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 text-center font-mono text-[9px] uppercase tracking-[0.22em] text-muted-foreground">
                  12 themes · free → enterprise
                </div>
              </div>
            </Reveal>
          </div>

          {/* Copy */}
          <div className="order-1 w-full min-w-0 lg:order-2 lg:w-1/2">
            <Reveal>
              <GeometricBadge tone="pink">BIO PAGES + THEME STUDIO</GeometricBadge>
              <h2 className="mt-5 font-display text-4xl font-extrabold leading-[0.95] tracking-tight md:text-5xl">
                Your bio. Your brand.
                <span className="ai-gradient-text"> One link that does it all.</span>
              </h2>
              <p className="mt-5 max-w-lg font-sans text-base leading-relaxed text-ink/70 md:text-lg">
                Give your audience one home for everything. Pick from 12 themes, drag to reorder,
                add your avatar and branding, and watch it update live on a phone preview — then
                every tap flows into the same analytics as your links.
              </p>
            </Reveal>

            {/* Handle / URL ownership hook */}
            <Reveal delay={100} className="mt-6">
              <div className="inline-flex items-center gap-2 rounded-full border-2 border-ink bg-white py-1.5 pl-4 pr-1.5 shadow-sticker-xs">
                <span className="font-mono text-sm text-ink/50">linklane.io/</span>
                <span className="font-display font-extrabold text-candy">@you</span>
              </div>
            </Reveal>

            <Reveal delay={160} className="mt-7 flex flex-wrap gap-2.5">
              {PERKS.map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-2 rounded-full border-2 border-ink bg-white px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-widest shadow-sticker-xs"
                >
                  <Icon size={12} strokeWidth={2.5} className="text-pink" />
                  {label}
                </span>
              ))}
            </Reveal>
            <Reveal delay={240} className="mt-8">
              <CandyButton to="/signup" variant="candy" withArrow>
                Claim your bio page
              </CandyButton>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
